import { z } from 'zod';

/**
 * Transport schemas for POST /api/v1/checkins — docs/02_OPENAPI.yaml.
 *
 * `email` stays optional here even though the demo event configures it as
 * required (docs/03_MOBILE_UI_SPEC.md §28.4). Required-ness belongs to the
 * deployed event config, so the API enforces it per event after this schema
 * has parsed the envelope; baking it into the transport schema would reject
 * pending submissions created under a config where it was optional.
 */
/**
 * Shared so the browser rejects exactly what the API rejects. A client-side
 * rule that is stricter than the server's silently blocks valid participants;
 * a looser one produces a 400 after the local save.
 */
export const EmailSchema = z.email().max(254);

/**
 * A Vietnamese phone number: a leading zero and 10 or 11 digits in total.
 *
 * Both lengths are real. Mobile numbers are 10 digits, and landlines written
 * with an area code run to 11. Applied to the value after
 * `normalizeVietnamPhone`, so spacing and a `+84` prefix are already gone by
 * the time this sees it, and shared for the same reason `EmailSchema` is: a
 * browser rule stricter than the server's blocks valid participants, and a
 * looser one produces a 400 after the local save.
 */
export const VietnamPhoneSchema = z
  .string()
  .regex(/^0\d{9,10}$/, 'must be a Vietnamese phone number starting with 0');

export const CheckinDataSchema = z.strictObject({
  full_name: z.string().min(1).max(150),
  phone: z.string().max(30).optional(),
  email: EmailSchema.optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
});

export const CheckinRequestSchema = z.strictObject({
  submission_id: z.uuid(),
  event_id: z.string().min(1).max(100),
  /**
   * Recorded verbatim, never compared against the deployed version
   * (AGENTS.md invariant 6).
   */
  event_config_version: z.int().min(1),
  location_id: z.string().min(1).max(100),
  client_created_at: z.iso.datetime({ offset: true }),
  /** Verified server-side then discarded. Never stored anywhere. */
  turnstile_token: z.string().min(1).max(2048),
  data: CheckinDataSchema,
});

/**
 * The 202 body. `z.object`, not `z.strictObject` — an unknown key is ignored.
 *
 * The impostor detection lives in the literals, not in the key set. A captive
 * portal or proxy answering on the API's behalf returns HTML, which never
 * survives `response.json()`, and anything that does reach this schema has to
 * produce `ok: true`, `status: 'accepted'` and a uuid `submission_id` to pass.
 * Rejecting an *extra* key catches no impostor that those three miss.
 *
 * What strictness did cost is a stuck participant. Add one key to this
 * response and every device still on a cached bundle fails the parse; the
 * client maps that to `NETWORK`, `NETWORK` is retryable, so the record stays
 * pending forever — a retry roughly every sixty seconds, each one burning a
 * fresh Turnstile challenge, for a submission the server accepted long ago.
 * The participant just watches an amber spinner that never resolves.
 *
 * So: do not "restore" the strictness. Every required field, both literals and
 * the uuid check are the contract; the key set is not.
 */
export const CheckinAcceptedResponseSchema = z.object({
  ok: z.literal(true),
  status: z.literal('accepted'),
  submission_id: z.uuid(),
});

/**
 * POST /api/v1/checkouts.
 *
 * `submission_id` is the check-in's, which is what makes a check-out its own
 * idempotency key: tapping the button twice, and a retry after a lost 202,
 * both collapse to one row. There is deliberately no location field — a
 * check-out belongs to a check-in that already recorded one.
 */
export const CheckoutRequestSchema = z.strictObject({
  submission_id: z.uuid(),
  event_id: z.string().min(1).max(100),
  client_checked_out_at: z.iso.datetime({ offset: true }),
  /** Verified server-side then discarded. Never stored anywhere. */
  turnstile_token: z.string().min(1).max(2048),
});

/**
 * The check-out 202 body, tolerant of unknown keys for exactly the reasons
 * spelled out on `CheckinAcceptedResponseSchema`: the literals are what unmask
 * an impostor, and strictness would strand a cached bundle in an endless
 * retry the first time this response grows a field.
 */
export const CheckoutAcceptedResponseSchema = z.object({
  ok: z.literal(true),
  status: z.literal('accepted'),
  submission_id: z.uuid(),
});

export type CheckinData = z.infer<typeof CheckinDataSchema>;
export type CheckinRequest = z.infer<typeof CheckinRequestSchema>;
export type CheckinAcceptedResponse = z.infer<typeof CheckinAcceptedResponseSchema>;
export type CheckoutRequest = z.infer<typeof CheckoutRequestSchema>;
export type CheckoutAcceptedResponse = z.infer<typeof CheckoutAcceptedResponseSchema>;
